import { MusicRNN } from '@magenta/music/es6/music_rnn';
import { NoteSequence } from '@magenta/music/es6/protobuf';
import { flattenChordProgression } from '../magenta-utils';
import { ChordProgression } from '../types';
import BaseGenerator, { BaseGeneratorOptions } from './BaseGenerator';

export type MusicRNNConditioners = {
  /** The softmax temperature to use when sampling. */
  temperature?: number;
  /** Array of chord symbol strings like "Cmaj7", "Abdim", "C#m7" */
  chordProgression?: ChordProgression|string[];
}

export interface MusicRNNConditioner {
  (context: any, progress: number): MusicRNNConditioners;
}

type MusicRNNGeneratorOptionsExtension = {
  checkpoint: string;
  conditionGenerator?: MusicRNNConditioner;
}

export type MusicRNNGeneratorOptions =
  BaseGeneratorOptions & MusicRNNGeneratorOptionsExtension;

const DEFAULT_CONDITIONERS: MusicRNNConditioners = {
  temperature: 1,
  chordProgression: ['Cmaj7', 'Abdim', 'C#m7'],
};

export default class MusicRNNGenerator extends BaseGenerator {
  private conditionGenerator: MusicRNNConditioner;

  private model!: MusicRNN;

  private modelCheckpoint!: string;

  get initialized(): boolean {
    return this.model.isInitialized();
  }

  get checkpoint(): string {
    return this.modelCheckpoint;
  }

  set checkpoint(checkpoint: string) {
    this.modelCheckpoint = checkpoint;
    this.model = new MusicRNN(checkpoint);
  }

  get acceptsChordProgression(): boolean {
    // Converting to `any` type to access private property `spec`
    const { chordEncoder } = (this.model as any);
    return Boolean(chordEncoder);
  }

  constructor(options: MusicRNNGeneratorOptions) {
    super(options);
    this.conditionGenerator = options.conditionGenerator || (() => DEFAULT_CONDITIONERS);
    this.checkpoint = options.checkpoint;
  }

  async initialize(): Promise<MusicRNNGenerator> {
    if (!this.initialized) {
      await this.model.initialize();
    }
    return this;
  }

  async generateSequence(seedSequence: NoteSequence, progress: number): Promise<NoteSequence> {
    // Get conditioning parameters
    const { temperature, chordProgression } = {
      ...DEFAULT_CONDITIONERS,
      ...this.conditionGenerator(this.context, progress),
    };
    let inputChordProgression;
    if (this.acceptsChordProgression && chordProgression) {
      inputChordProgression = ((Array.isArray(chordProgression)
        ? chordProgression
        : flattenChordProgression(chordProgression)));
    }
    const sequence = await this.model.continueSequence(
      seedSequence,
      Math.floor(this.sequenceDurationSeconds * this.stepsPerSecond),
      temperature,
      inputChordProgression,
    );
    return new NoteSequence(sequence);
  }
}
